import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import QrScanner from "qr-scanner";
import { BrandMark } from "../art/Icons";
import { BRAND_NAME } from "../../config/event";
import { detectTableNumber, getActivityByTableNumber } from "../../config/activities";

const SAVE_ENDPOINT = "/backend/save_scanner_data.php";
const SCANNER_OPTIONS = { preferredCamera: "environment", highlightScanRegion: true, highlightCodeOutline: true };

/** Translate a decoded QR payload (or raw text) into the row stored by the backend. */
export const buildScanPayload = (data) => {
  const fallbackTime = new Date().toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
  return {
    time_slot: data.timeSlot || data.time_slot || data.rawData || fallbackTime,
    team_name: data.teamName || data.team_name || data.rawData || `Scanned_${Date.now()}`,
    table: detectTableNumber(data),
  };
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString("cs-CZ") : "N/A");

const ScanResult = ({ data }) => {
  if (data.rawData) {
    return (
      <div className="modal-box">
        <h4>Naskenovaná data</h4>
        <p className="break-all">{data.rawData}</p>
        <p className="text-xs mt-2">Naskenováno: {formatDateTime(data.timestamp)}</p>
      </div>
    );
  }
  const activity = getActivityByTableNumber(detectTableNumber(data));
  return (
    <div className="modal-box">
      <h4>Informace o rezervaci</h4>
      <div className="kv">
        <span>
          <b>ID rezervace:</b> {data.reservationId ?? "N/A"}
        </span>
        <span>
          <b>Tým:</b> {data.teamName ?? "N/A"}
        </span>
        <span>
          <b>Počet účastníků:</b> {data.participantCount ?? "N/A"}
        </span>
        <span>
          <b>Čas:</b> {data.timeSlot ?? "N/A"}
        </span>
        <span>
          <b>Aktivita:</b> {activity?.title ?? "N/A"}
        </span>
        <span>
          <b>Datum:</b> {data.date ?? "N/A"}
        </span>
        <span>
          <b>Naskenováno:</b> {formatDateTime(data.timestamp)}
        </span>
      </div>
    </div>
  );
};

const QRScanner = () => {
  const scannerRef = useRef(null);
  const videoRef = useRef(null);
  const isProcessingRef = useRef(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState("");
  const [scannedData, setScannedData] = useState(null);

  const saveScan = async (data) => {
    try {
      const response = await fetch(SAVE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildScanPayload(data)),
      });
      if (!response.ok) {
        setError(`Uložení skenu selhalo (HTTP ${response.status})`);
        return;
      }
      setError("");
    } catch (err) {
      setError(`Chyba při ukládání skenu: ${err.message}`);
    }
  };

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.stop();
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
    setIsScanning(false);
  };

  const onScanSuccess = async (result) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    let data;
    try {
      data = JSON.parse(result.data);
    } catch (parseError) {
      data = { rawData: result.data, timestamp: new Date().toISOString() };
    }
    setScannedData(data);
    await saveScan(data);
    stopScanning();
    isProcessingRef.current = false;
  };

  const startScanning = async () => {
    setError("");
    setScannedData(null);
    setIsScanning(true);
    try {
      if (!videoRef.current || scannerRef.current) return;
      if (!(await QrScanner.hasCamera())) {
        setError("Kamera není dostupná na tomto zařízení");
        setIsScanning(false);
        return;
      }
      scannerRef.current = new QrScanner(videoRef.current, onScanSuccess, { ...SCANNER_OPTIONS, onDecodeError: () => {} });
      await scannerRef.current.start();
    } catch (err) {
      setError(`Chyba při spouštění kamery: ${err.message}`);
      setIsScanning(false);
    }
  };

  useEffect(
    () => () => {
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
      }
    },
    []
  );

  return (
    <div className="scanner-page">
      <Link className="brand" to="/">
        <BrandMark />
        {BRAND_NAME}
      </Link>
      <h1 className="display-h1 text-center" style={{ fontSize: "clamp(26px, 4vw, 40px)" }}>
        SKENER QR KÓDŮ
      </h1>

      {error && (
        <p className="msg error w-full max-w-md" role="alert">
          {error}
        </p>
      )}

      <div className="qr-reader">
        <video ref={videoRef} hidden={!isScanning} playsInline muted autoPlay />
        {!isScanning && (
          <div className="qr-idle">
            <span className="text-3xl" aria-hidden="true">
              📷
            </span>
            <p>Kamera se aktivuje po kliknutí na „Spustit skenování“.</p>
          </div>
        )}
        {isScanning && (
          <>
            <div className="scan-frame" />
            <div className="scan-hint">Namířte kameru na QR kód</div>
          </>
        )}
      </div>

      {!isScanning && !scannedData && (
        <button type="button" className="btn btn-marigold" onClick={startScanning}>
          Spustit skenování
        </button>
      )}
      {isScanning && (
        <button type="button" className="btn btn-ghost" onClick={stopScanning}>
          Zastavit skenování
        </button>
      )}

      {scannedData && (
        <div className="card w-full max-w-md">
          <ScanResult data={scannedData} />
          <button
            type="button"
            className="btn btn-agua"
            onClick={() => {
              setScannedData(null);
              setError("");
            }}
          >
            Skenovat znovu
          </button>
        </div>
      )}
    </div>
  );
};

export default QRScanner;
